import * as React from 'react';
import { Table } from '@leagueplatform/genesis-core-alpha';
import {
  MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { HtmlToReact, GDSBackgroundColor } from '@leagueplatform/genesis-core';
import { useUniqueId } from '@leagueplatform/web-common';

interface BasicTable {
  tableName: string;
  hasBorder?: boolean;
  headerBackgroundColor?: GDSBackgroundColor;
  headers: Header[];
  rows: Row[];
}

interface Header {
  name: string;
  id: string;
}

interface Row {
  row: string[];
}

export type MasonryBasicTableNode = MasonryEngineNode<
  'basicTable',
  BasicTable,
  {},
  {}
>;

export type MasonryBasicTableRendererProps =
  MasonryEngineNodeRendererProps<MasonryBasicTableNode>;

export const MasonryBasicTableRenderer = ({
  tableName,
  hasBorder,
  headerBackgroundColor = 'secondary',
  headers,
  rows,
  locatorId,
  css,
  rootElementRef,
}: MasonryBasicTableRendererProps) => {
  const uniqueId = useUniqueId();
  if (!headers?.length || !rows?.length) return null;

  return (
    <Table
      forwardedRef={rootElementRef}
      data-testid={locatorId}
      tableName={tableName}
      border={hasBorder}
      isResponsive
      css={css}
    >
      <Table.Head>
        <Table.Row>
          {headers.map((header) => (
            <Table.Header
              key={header.id}
              label={header.name}
              backgroundColor={headerBackgroundColor}
            />
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {rows.map((row, rowIndex) => (
          <Table.Row
            // eslint-disable-next-line react/no-array-index-key
            key={`${uniqueId}-${rowIndex}`}
          >
            {row.row.map((cell, cellIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <Table.Cell key={`${uniqueId}-${rowIndex}-${cellIndex}`}>
                <HtmlToReact htmlString={cell} />
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};
